import React, { ReactNode } from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles } from '../styles/typography';
import PDFHeader from './PDFHeader';
import PDFMetadataFooter, { PDFMetadataFooterData } from './PDFMetadataFooter';
import { createT } from '../utils/simpleTranslate';

// Import font registration to ensure fonts are loaded
import '../styles/fonts';

/**
 * Base PDF Document Component
 * 
 * Replaces the current BasePDF component with @react-pdf/renderer.
 * Provides the foundational structure for all PDF documents.
 */

export interface PDFConfig {
  hospitalName: string;
  logoBlob: string;
  locale: string;
  address: string;
  city: string;
  email: string;
  phone: string;
}

interface PDFDocumentProps {
  title?: string | ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  config: PDFConfig;
  /** When provided, overrides config.locale for document strings and header (e.g. user's UI language). */
  locale?: string;
  pageSize?: 'A4' | 'LETTER';
  orientation?: 'portrait' | 'landscape';
  showHeader?: boolean;
  patientInfo?: ReactNode;
  /** When provided, renders a fixed metadata footer on every page (patient, clinic, document ID, page numbers). */
  metadataFooter?: PDFMetadataFooterData | null;
}

/**
 * Base PDF Document component using @react-pdf/renderer
 */
export const PDFDocument: React.FC<PDFDocumentProps> = ({
  patientInfo,
  title,
  subtitle,
  children,
  footer,
  config,
  locale: localeProp,
  pageSize = 'A4',
  orientation = 'portrait',
  showHeader = true,
  metadataFooter,
}) => {
  const locale = localeProp ?? config.locale;
  const t = createT(locale);
  
  // Convert title to string for document metadata
  const documentTitle = typeof title === 'string' ? title : t('general.document', 'Document PDF');

  const pageStyle = metadataFooter
    ? { ...commonPDFStyles.page, paddingBottom: 85 }
    : commonPDFStyles.page;

  return (
    <Document
      title={documentTitle}
      author={config.hospitalName}
      subject={t('hospital.document.medical')}
      creator={t('general.system_name')}
    >
      <Page size={pageSize} orientation={orientation} style={pageStyle} wrap>
        {/* Header Section */}
        {showHeader && (
          <PDFHeader
            hospitalName={config.hospitalName}
            logoBlob={config.logoBlob}
            locale={locale}
            address={config.address}
            city={config.city}
            email={config.email}
            phone={config.phone}
            title={title}
          />
        )}

        {patientInfo && (
          <View style={{ marginBottom: 5 }}>
            {patientInfo}
            <View style={{ borderBottomWidth: 1, borderBottomColor: '#E0E0E0' }} />
          </View>
        )}

        {/* Subtitle Section */}
        {subtitle && (
          <View style={{ marginBottom: 15 }}>
            {typeof subtitle === 'string' ? (
              <Text style={typographyStyles.subtitle1}>{subtitle}</Text>
            ) : (
              subtitle
            )}
          </View>
        )}

        {/* Main Content */}
        <View style={commonPDFStyles.container}>
          {children}
        </View>

        {/* Footer Section */}
        {footer && (
          <View>
            {footer}
          </View>
        )}

        {/* Fixed metadata footer on every page (patient, clinic, document ID, page numbers) */}
        {metadataFooter && <PDFMetadataFooter data={metadataFooter} />}
      </Page>
    </Document>
  );
};

export default PDFDocument;
